import { Component, h, Element, Event, EventEmitter, Method } from '@stencil/core';
import { isBreakpointEqualOrSmaller, createUuid, getCurrentBreakpoint } from '../../utils';
@Component({
  tag: 'dc-ui-header',
  styleUrl: 'dc-ui-header.scss',
  shadow: true,
})
export class DcUiHeader {
  @Element() el!: HTMLElement;

  private headerRef?: HTMLElement;
  private lastScrollY = 0;
  private headerHeight = 0;
  private metaNavElements: HTMLElement[] = [];
  private metaNavSlot?: HTMLElement;
  private mainNavLanguageSlot?: HTMLElement;
  private originalPositions = new Map<string, number>();
  private insideTrigger = false;
  private mainNavElement?: HTMLDcUiHeaderMainNavElement;
  private metaNavObserver: MutationObserver;
  private currentBreakpoint: string = getCurrentBreakpoint();

  @Event() dcHeaderHidden: EventEmitter<void>;
  @Event() dcHeaderShown: EventEmitter<void>;
  @Event() languageButtonMoved: EventEmitter<void>;

  @Method()
  async manageSession(sessionKey: string) {
    // delete all the other session keys but the one we want to keep
    Object.keys(sessionStorage).forEach((key) => {
      if (key !== sessionKey) {
        sessionStorage.removeItem(key);
      }
    });

    this.insideTrigger = true;
  }

  componentDidLoad() {
    this.headerRef = this.el.shadowRoot?.querySelector('.header-wrapper');
    this.headerHeight = this.headerRef?.offsetHeight || 0;

    // Get references to elements
    this.metaNavSlot = this.el.querySelector('dc-ui-header-base');
    this.mainNavLanguageSlot = this.el.querySelector('dc-ui-header-main-nav');
    this.mainNavElement = this.el.querySelector('dc-ui-header-main-nav');

    // Add event listeners
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);

    // Store all meta-nav elements and their positions with unique IDs
    this.storeMetaNavElements();

    // Setup observer for meta nav changes
    this.setupMetaNavObserver();

    // Initial position check
    this.updateLanguageButtonPosition();

    // Add beforeunload listener
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  private setupMetaNavObserver() {
    if (this.metaNavSlot) {
      this.metaNavObserver = new MutationObserver((mutations) => {
        const hasChildListChanges = mutations.some(
          (mutation) =>
            mutation.type === 'childList' &&
            (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)
        );

        if (hasChildListChanges) {
          // Store elements and update positions
          this.storeMetaNavElements();
          this.updateLanguageButtonPosition();
        }
      });

      this.metaNavObserver.observe(this.metaNavSlot, {
        childList: true,
      });
    }
  }

  private storeMetaNavElements() {
    if (this.metaNavSlot) {
      this.metaNavElements = Array.from(
        this.el.querySelectorAll('dc-ui-meta-nav-button, dc-ui-meta-nav-link')
      );

      const currentParent = this.metaNavElements[0].parentElement;
      const children = Array.from(currentParent.children);

      this.metaNavElements.forEach((el) => {
        // Use existing ID or create a unique one
        const id = el.id || `meta-nav-${createUuid()}`;
        if (!el.id) el.id = id;
        this.originalPositions.set(id, children.indexOf(el));
      });
    }
  }

  componentDidRender() {
    if (this.mainNavElement) {
      this.mainNavElement.checkForOverflow();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    // Disconnect the observer
    if (this.metaNavObserver) {
      this.metaNavObserver.disconnect();
    }
  }

  private readonly handleResize = () => {
    const newBreakpoint = getCurrentBreakpoint();
    if (this.currentBreakpoint !== newBreakpoint) {
      // Disconnect the observer after first mutation
      this.metaNavObserver.disconnect();

      this.currentBreakpoint = newBreakpoint;
      this.updateLanguageButtonPosition();
    }
  };

  private moveElement(
    element: HTMLElement,
    targetParent: HTMLElement,
    referencePosition: number | null,
    useSmallLayout: boolean
  ) {
    const currentParent = element.parentElement;

    if (currentParent === targetParent) return;

    currentParent?.removeChild(element);

    if (targetParent === this.mainNavLanguageSlot) {
      element.setAttribute('data-in-main-nav', '');
    } else {
      element.removeAttribute('data-in-main-nav');
    }

    if (!useSmallLayout && targetParent.children.length > 0 && referencePosition !== null) {
      const referenceNode = targetParent.children[referencePosition] || null;
      targetParent.insertBefore(element, referenceNode);
    } else {
      targetParent.appendChild(element);
    }
  }

  private readonly updateLanguageButtonPosition = () => {
    if (!this.metaNavSlot || !this.mainNavLanguageSlot) return;

    // Handle language button
    const languageButton = this.metaNavElements.find((el) => el.hasAttribute('language'));
    if (languageButton) {
      const targetParent = isBreakpointEqualOrSmaller('xl')
        ? this.mainNavLanguageSlot
        : this.metaNavSlot;
      const position = this.originalPositions.get(languageButton.id);
      this.moveElement(
        languageButton,
        targetParent,
        position ?? null,
        isBreakpointEqualOrSmaller('xl')
      );
    }

    // Handle other meta-nav elements
    const otherMetaNavElements = this.metaNavElements.filter((el) => !el.hasAttribute('language'));
    const isMdOrSmaller = isBreakpointEqualOrSmaller('md');

    otherMetaNavElements.forEach((element) => {
      const targetParent = isMdOrSmaller ? this.mainNavLanguageSlot : this.metaNavSlot;
      const position = this.originalPositions.get(element.id);
      this.moveElement(element, targetParent, position ?? null, isMdOrSmaller);
    });

    this.languageButtonMoved.emit();
  };

  private readonly handleScroll = () => {
    const currentScrollY = window.scrollY;

    if (currentScrollY > this.headerHeight) {
      if (currentScrollY > this.lastScrollY) {
        // Scrolling down - hide header
        this.headerRef?.classList.add('header--hidden');
        this.dcHeaderHidden.emit();
      } else {
        // Scrolling up - show header
        this.headerRef?.classList.remove('header--hidden');
        this.dcHeaderShown.emit();
      }
    } else {
      // At the top - ensure header is visible
      this.headerRef?.classList.remove('header--hidden');
      this.dcHeaderShown.emit();
    }

    this.lastScrollY = currentScrollY;
  };

  private readonly handleBeforeUnload = () => {
    if (this.insideTrigger) {
      this.insideTrigger = false;
      return;
    }

    // because the trigger is not inside the component, we can remove the session keys beginning with 'nav-path-'
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('nav-path-')) {
        sessionStorage.removeItem(key);
      }
    });
  };

  render() {
    const hasBase = this.el.querySelector('[slot="base"]');
    const hasMainNav = this.el.querySelector('[slot="main-nav"]');

    const base = hasBase ? (
      <div class="header-base-wrapper">
        <slot name="base" />
      </div>
    ) : null;

    const mainNav = hasMainNav ? (
      <div class="header-main-nav-wrapper">
        <slot name="main-nav" />
      </div>
    ) : null;

    return (
      <header class="header-wrapper">
        {base}
        {mainNav}

        <slot name="popover" />
      </header>
    );
  }
}
